<template>
  <div class="cashback-restricoes">
    <CRow class="align-items-end">
      <CCol md="3">
        <label class="cashback-regra__rotulo">Tipo</label>
        <select class="form-control" v-model="nova.tipo" @change="aoTrocarTipo">
          <option v-for="tipo in tipos" :key="tipo.chave" :value="tipo.chave">{{ tipo.label }}</option>
        </select>
      </CCol>
      <CCol md="3" v-if="ehDimensao">
        <label class="cashback-regra__rotulo">Nível</label>
        <select class="form-control" v-model="nova.dimensaoColuna" @change="nova.chave = null">
          <option v-for="dimensao in dimensoes" :key="dimensao.coluna" :value="dimensao.coluna">{{ dimensao.label }}</option>
        </select>
      </CCol>
      <CCol :md="ehDimensao ? 4 : 7">
        <label class="cashback-regra__rotulo">Código</label>
        <ComboCodigo
          :input-id="`cashback-restricao-${nova.tipo}-${nova.dimensaoColuna || ''}`"
          v-model="nova.chave"
          :opcoes="opcoesDoTipo"
          :codigo-livre="true"
          :busca-async="ehBuscaAsync"
          :carregando="busca.carregando"
          :placeholder="ehBuscaAsync ? 'Digite o código ou parte do nome' : 'Digite ou selecione o código'"
          @search="aoBuscar"
        />
      </CCol>
      <CCol md="2">
        <CButton color="primary" block :disabled="!podeAdicionar" @click="adicionar">
          <fa-icon icon="plus" class="mr-1" /> Incluir
        </CButton>
      </CCol>
    </CRow>

    <div v-if="chaveLonga || nova.tipo === 'PRODUTO_PRINCIPAL'" class="cashback-restricoes__dicas">
      <small v-if="chaveLonga" class="cashback-restricoes__dica cashback-restricoes__dica--erro">
        O código pode ter no máximo {{ tamanhoMaximoChave }} caracteres.
      </small>
      <small v-if="nova.tipo === 'PRODUTO_PRINCIPAL'" class="cashback-restricoes__dica">
        Lista só os produtos que são principais de outros itens no cadastro do ERP.
      </small>
    </div>

    <div class="cashback-restricoes__quadro">
      <div class="cashback-restricoes__cabecalho">
        <template v-if="grupos.length">
          {{ sujeito }} <strong>{{ exigeTodas ? "todas as linhas" : "qualquer uma das linhas" }}</strong> abaixo.
          <template v-if="algumaLinhaComVarios">Dentro de uma mesma linha, basta um dos valores.</template>
        </template>
        <template v-else>{{ vazio }}</template>
      </div>

      <template v-for="(grupo, indice) in grupos">
        <div v-if="indice" :key="`conector-${grupo.chave}`" class="cashback-restricoes__conector">
          <span :class="['cashback-restricoes__conector-texto', exigeTodas ? 'cashback-restricoes__conector-texto--e' : 'cashback-restricoes__conector-texto--ou']">
            {{ exigeTodas ? "E" : "OU" }}
          </span>
        </div>
        <div :key="grupo.chave" class="cashback-restricoes__linha">
          <div class="cashback-restricoes__linha-tipo">{{ grupo.rotulo }}</div>
          <div class="cashback-restricoes__linha-valores">
            <span v-if="grupo.restricoes.length > 1" class="cashback-restricoes__linha-dica">um destes:</span>
            <span v-for="restricao in grupo.restricoes" :key="chaveDe(restricao)" class="cashback-restricoes__valor">
              {{ descricaoDe(restricao) }}
              <button
                type="button"
                class="cashback-restricoes__remover"
                title="Remover"
                :aria-label="`Remover ${descricaoDe(restricao)}`"
                @click="remover(restricao)"
              >
                &times;
              </button>
            </span>
          </div>
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import _debounce from "lodash/debounce";
import ComboCodigo from "@/components/form/ComboCodigo.vue";
import { get } from "@/helpers/apiRequest";
import { DEBOUNCE_BUSCA_MS, TAMANHO_MAXIMO_CHAVE_RESTRICAO } from "../../helpers";

const TIPOS_BUSCA_ASYNC = ["PRODUTO", "PRODUTO_PRINCIPAL", "CLIENTE"];

export default {
  name: "RestricoesCashback",
  components: { ComboCodigo },

  props: {
    value: { type: Array, default: () => [] },
    tipos: { type: Array, default: () => [] },
    valores: { type: Object, default: () => ({}) },
    dimensoes: { type: Array, default: () => [] },
    vazio: { type: String, default: "Nenhuma restrição cadastrada." },
    sujeito: { type: String, default: "Vale" },
    exigeTodas: { type: Boolean, default: true },
  },

  data() {
    return {
      nova: { tipo: null, chave: null, dimensaoColuna: null },
      busca: { termo: "", opcoes: [], carregando: false },
      tamanhoMaximoChave: TAMANHO_MAXIMO_CHAVE_RESTRICAO,
    };
  },

  computed: {
    chavesDosTipos() {
      return this.tipos.map((tipo) => tipo.chave);
    },

    restricoesDaAba() {
      return this.value.filter((restricao) => this.chavesDosTipos.includes(restricao.tipo));
    },

    grupos() {
      return this.restricoesDaAba.reduce((grupos, restricao) => {
        const chave = `${restricao.tipo}|${restricao.dimensaoColuna || ""}`;
        let grupo = grupos.find((item) => item.chave === chave);

        if (!grupo) {
          grupo = { chave, rotulo: this.rotuloTipo(restricao), restricoes: [] };
          grupos.push(grupo);
        }

        grupo.restricoes.push(restricao);

        return grupos;
      }, []);
    },

    algumaLinhaComVarios() {
      return this.grupos.some((grupo) => grupo.restricoes.length > 1);
    },

    ehDimensao() {
      return this.nova.tipo === "DIMENSAO";
    },

    dimensaoSelecionada() {
      return this.dimensoes.find((dimensao) => String(dimensao.coluna) === String(this.nova.dimensaoColuna)) || null;
    },

    ehBuscaAsync() {
      return TIPOS_BUSCA_ASYNC.includes(this.nova.tipo);
    },

    opcoesDoTipo() {
      if (this.ehBuscaAsync) return this.busca.opcoes;

      if (this.ehDimensao) {
        return this.dimensaoSelecionada ? this.dimensaoSelecionada.opcoes : [];
      }

      return this.valores[this.nova.tipo] || [];
    },

    chaveLonga() {
      return this.nova.chave !== null && String(this.nova.chave).trim().length > TAMANHO_MAXIMO_CHAVE_RESTRICAO;
    },

    podeAdicionar() {
      const temChave = this.nova.chave !== null && String(this.nova.chave).trim() !== "";

      return !!this.nova.tipo && temChave && !this.chaveLonga && (!this.ehDimensao || !!this.dimensaoSelecionada);
    },
  },

  watch: {
    tipos: {
      immediate: true,
      handler(tipos) {
        if (!tipos.some((tipo) => tipo.chave === this.nova.tipo)) {
          this.nova.tipo = tipos.length ? tipos[0].chave : null;
          this.aoTrocarTipo();
        }
      },
    },

    dimensoes() {
      if (this.ehDimensao && !this.dimensaoSelecionada) this.aoTrocarTipo();
    },
  },

  methods: {
    aoTrocarTipo() {
      this.nova.chave = null;
      this.busca = { termo: "", opcoes: [], carregando: false };
      this.nova.dimensaoColuna = this.ehDimensao && this.dimensoes.length ? this.dimensoes[0].coluna : null;
    },

    adicionar() {
      const restricao = {
        tipo: this.nova.tipo,
        chave: String(this.nova.chave).trim(),
        dimensaoColuna: this.ehDimensao ? this.nova.dimensaoColuna : null,
      };

      if (this.value.some((existente) => this.chaveDe(existente) === this.chaveDe(restricao))) return;

      const escolhida = this.busca.opcoes.find((opcao) => String(opcao.id) === restricao.chave);

      if (escolhida) restricao.rotulo = escolhida.descricao;

      this.$emit("input", [...this.value, restricao]);
      this.nova.chave = null;
    },

    remover(restricao) {
      this.$emit("input", this.value.filter((existente) => this.chaveDe(existente) !== this.chaveDe(restricao)));
    },

    chaveDe(restricao) {
      return `${restricao.tipo}|${restricao.chave}|${restricao.dimensaoColuna || ""}`;
    },

    rotuloTipo(restricao) {
      const tipo = this.tipos.find((item) => item.chave === restricao.tipo);

      if (restricao.tipo === "DIMENSAO") {
        const dimensao = this.dimensoes.find((item) => String(item.coluna) === String(restricao.dimensaoColuna));

        return dimensao ? dimensao.label : `${tipo ? tipo.label : restricao.tipo} ${restricao.dimensaoColuna}`;
      }

      return tipo ? tipo.label : restricao.tipo;
    },

    aoBuscar(termo) {
      this.busca.termo = String(termo || "").trim();

      if (!this.busca.termo) {
        this.busca.opcoes = [];
        this.busca.carregando = false;

        return;
      }

      this.busca.carregando = true;
      this.buscarComDebounce();
    },

    buscarComDebounce: _debounce(function () {
      const { termo } = this.busca;
      const tipo = this.nova.tipo;

      get(`/admin/cashback/regras/opcoes/${tipo}?q=${encodeURIComponent(termo)}`)
        .then((lista) => {
          if (termo !== this.busca.termo || tipo !== this.nova.tipo) return;

          this.busca.opcoes = (lista || []).map((opcao) => ({ id: opcao.chave, descricao: opcao.label }));
        })
        .catch(() => {
          if (termo === this.busca.termo) this.busca.opcoes = [];
        })
        .finally(() => {
          if (termo === this.busca.termo) this.busca.carregando = false;
        });
    }, DEBOUNCE_BUSCA_MS),

    descricaoDe(restricao) {
      const lista = restricao.tipo === "DIMENSAO"
        ? (this.dimensoes.find((item) => String(item.coluna) === String(restricao.dimensaoColuna)) || { opcoes: [] }).opcoes
        : this.valores[restricao.tipo] || [];
      const opcao = lista.find((item) => String(item.id) === String(restricao.chave));

      if (opcao && opcao.descricao !== String(restricao.chave)) return `${restricao.chave} - ${opcao.descricao}`;

      if (restricao.rotulo && restricao.rotulo !== String(restricao.chave)) return `${restricao.chave} - ${restricao.rotulo}`;

      return restricao.chave;
    },
  },
};
</script>

<style scoped>
.cashback-regra__rotulo {
  display: block;
  font-size: 11px;
  font-weight: 600;
  color: #667085;
  text-transform: uppercase;
  margin-bottom: 4px;
}

.cashback-restricoes__dica {
  display: block;
  margin-top: 4px;
  font-size: 11px;
  color: #98a2b3;
}

.cashback-restricoes__dica--erro {
  color: #d92d20;
}

.cashback-restricoes__quadro {
  margin-top: 14px;
  border: 1px solid #eaecf0;
  border-radius: 10px;
  background-color: #fff;
  overflow: hidden;
}

.cashback-restricoes__cabecalho {
  padding: 10px 14px;
  border-bottom: 1px solid #eaecf0;
  background-color: #f9fafb;
  font-size: 12.5px;
  color: #475467;
}

.cashback-restricoes__cabecalho strong {
  color: var(--cashback-cor-primaria);
}

.cashback-restricoes__linha {
  display: grid;
  grid-template-columns: 170px minmax(0, 1fr);
  gap: 12px;
  align-items: center;
  padding: 10px 14px;
}

.cashback-restricoes__linha-tipo {
  font-size: 12px;
  font-weight: 600;
  color: #344054;
}

.cashback-restricoes__linha-valores {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
}

.cashback-restricoes__linha-dica {
  font-size: 11.5px;
  color: #98a2b3;
}

.cashback-restricoes__valor {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 3px 4px 3px 10px;
  border-radius: 12px;
  background-color: var(--cashback-cor-primaria-suave);
  font-size: 12px;
  color: var(--cashback-cor-primaria);
}

.cashback-restricoes__remover {
  padding: 0;
  border: none;
  background: none;
  line-height: 1;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-weight: 700;
  color: var(--cashback-cor-primaria);
}

.cashback-restricoes__remover:hover {
  background-color: #d92d20;
  color: #fff;
}

.cashback-restricoes__conector {
  position: relative;
  height: 0;
  border-top: 1px dashed #d0d5dd;
  margin: 0 14px;
}

.cashback-restricoes__conector-texto {
  position: absolute;
  top: -9px;
  left: 170px;
  padding: 0 8px;
  border-radius: 9px;
  font-size: 10.5px;
  font-weight: 700;
  line-height: 18px;
  letter-spacing: 0.5px;
  color: #fff;
}

.cashback-restricoes__conector-texto--e {
  background-color: var(--cashback-cor-primaria);
}

.cashback-restricoes__conector-texto--ou {
  background-color: #2298a3;
}
</style>
